import type {CalendarDate} from "@/lib/birthdate";

const MINIMUM_AGE_YEARS = 14;

/**
 * Whether a visitor with the given birthdate has reached the minimum age (14).
 *
 * Two flow steps are gated on this: visitors below the minimum age skip the
 * Terms/consent step (they cannot give consent themselves) and the email step.
 * The birthday counts the moment it arrives — someone whose 14th birthday is
 * exactly today qualifies, while someone who turns 14 tomorrow does not.
 * Equivalent to `today >= birthdate + 14 years`.
 *
 * `birthdate` arrives as the `CalendarDate` the birthdate step stores. That step
 * only stores a fully valid date, so an absent value means "not collected"; we
 * default to qualifying (show Terms, ask for email) rather than silently
 * dropping an adult.
 */
export function meetsMinimumAge(birthdate: CalendarDate | undefined): boolean {
    if (!birthdate) return true;

    const minimumBirthday = new Date(birthdate.year + MINIMUM_AGE_YEARS, birthdate.month - 1, birthdate.day);
    minimumBirthday.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return today >= minimumBirthday;
}

/**
 * Resolves the minimum-age gate from whichever signal the venue collected.
 *
 * Venues that collect a precise date of birth derive the gate from the entered
 * date (`meetsMinimumAge`). Venues that instead show the age-gate checkboxes
 * store a self-declared `isOfMinimumAge` boolean and no birthdate. The
 * declaration takes precedence when present; otherwise we fall back to the
 * birthdate (which defaults to qualifying when absent, matching
 * `meetsMinimumAge`). Both the client flow and the submit route consult this so
 * the under-14 skip logic stays consistent across modes.
 */
export function userMeetsMinimumAge(user: {birthdate?: CalendarDate; isOfMinimumAge?: boolean}): boolean {
    if (user.isOfMinimumAge !== undefined) return user.isOfMinimumAge;
    return meetsMinimumAge(user.birthdate);
}
